import { useMemo } from 'react';
import { evaluateFormula } from '@solote/contracts';
import { Badge, Input, Select, Textarea, describedBy } from '@/design-system';
import {
  formatCents,
  formatNumber,
  maskCep,
  maskCnpj,
  maskCpf,
  maskCurrencyFromDigits,
  maskPhone,
  parseCentsFromMasked,
} from '@/core/format/br';
import { GalleryControl, VideoControl } from '../controls/MediaControls';
import { registerFieldType, type FieldComponentProps } from '../field-registry';
import { PasswordControl } from './PasswordControl';

/**
 * CONTROLES PADRAO
 *
 * Um controle por familia de tipo. Nenhum deles conhece "lote", "preco" ou
 * "topografia" — conhecem apenas `FieldSchema`. E por isso que o mesmo motor
 * atende o cadastro de lote hoje e o cadastro rural do SoFazenda depois
 * (Cap. 36), sem uma linha nova.
 */

function affixed(field: FieldComponentProps['field'], input: React.ReactNode) {
  const { prefix, suffix } = field.presentation;
  if (!prefix && !suffix) return input;
  return (
    <div className="ds-input-group">
      {prefix ? <span className="ds-input-group__affix">{prefix}</span> : null}
      {input}
      {suffix ? <span className="ds-input-group__affix">{suffix}</span> : null}
    </div>
  );
}

function TextControl({ field, value, onChange, onBlur, error, disabled, inputId }: FieldComponentProps) {
  const shared = {
    id: inputId,
    value: typeof value === 'string' ? value : '',
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(event.target.value),
    onBlur,
    disabled,
    placeholder: field.presentation.placeholder ?? undefined,
    'aria-invalid': Boolean(error),
    'aria-describedby': describedBy(inputId, field.presentation.help, error),
    maxLength: field.validation.maxLength ?? undefined,
  };

  if (field.type === 'textarea' || field.type === 'richtext') {
    return <Textarea {...shared} rows={field.type === 'richtext' ? 10 : 4} />;
  }

  const inputType = field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text';
  return affixed(field, <Input {...shared} type={inputType} inputMode={field.type === 'email' ? 'email' : undefined} />);
}

const MASKS: Record<string, (value: string) => string> = {
  cpf: maskCpf,
  cnpj: maskCnpj,
  cep: maskCep,
  phone: maskPhone,
  whatsapp: maskPhone,
};

function MaskedControl({ field, value, onChange, onBlur, error, disabled, inputId }: FieldComponentProps) {
  const mask = MASKS[field.type];
  const display = typeof value === 'string' ? (mask ? mask(value) : value) : '';

  return (
    <Input
      id={inputId}
      value={display}
      // Guardamos o valor mascarado: o servico normaliza para digitos na
      // borda. Assim o usuario ve o que digitou mesmo apos erro de validacao.
      onChange={(event) => onChange(mask ? mask(event.target.value) : event.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      inputMode="numeric"
      autoComplete={field.type === 'cep' ? 'postal-code' : field.type === 'phone' ? 'tel' : 'off'}
      placeholder={field.presentation.placeholder ?? undefined}
      aria-invalid={Boolean(error)}
      aria-describedby={describedBy(inputId, field.presentation.help, error)}
    />
  );
}

function NumericControl({ field, value, onChange, onBlur, error, disabled, inputId }: FieldComponentProps) {
  return affixed(
    field,
    <Input
      id={inputId}
      type="number"
      inputMode="decimal"
      value={value === null || value === undefined ? '' : String(value)}
      onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
      onBlur={onBlur}
      disabled={disabled}
      min={field.validation.min ?? undefined}
      max={field.validation.max ?? undefined}
      step="any"
      placeholder={field.presentation.placeholder ?? undefined}
      aria-invalid={Boolean(error)}
      aria-describedby={describedBy(inputId, field.presentation.help, error)}
    />,
  );
}

/** Dinheiro digita da direita para a esquerda e sai em centavos (inteiro). */
function CurrencyControl({ field, value, onChange, onBlur, error, disabled, inputId }: FieldComponentProps) {
  const display = typeof value === 'number' ? formatCents(value) : '';

  return (
    <Input
      id={inputId}
      value={display}
      onChange={(event) => onChange(parseCentsFromMasked(maskCurrencyFromDigits(event.target.value)))}
      onBlur={onBlur}
      disabled={disabled}
      inputMode="numeric"
      placeholder={field.presentation.placeholder ?? 'R$ 0,00'}
      aria-invalid={Boolean(error)}
      aria-describedby={describedBy(inputId, field.presentation.help, error)}
    />
  );
}

function SelectControl({ field, value, onChange, onBlur, error, disabled, options, inputId }: FieldComponentProps) {
  return (
    <Select
      id={inputId}
      value={typeof value === 'string' ? value : ''}
      onChange={(event) => onChange(event.target.value || null)}
      onBlur={onBlur}
      disabled={disabled || options.length === 0}
      aria-invalid={Boolean(error)}
      aria-describedby={describedBy(inputId, field.presentation.help, error)}
    >
      <option value="">{field.presentation.placeholder ?? 'Selecione…'}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Select>
  );
}

/**
 * CIDADE E BAIRRO — sugere, mas não fecha a porta.
 *
 * Os dois eram `<select>` alimentado por uma lista de cinco cidades. Quem
 * anunciava na sexta simplesmente **não conseguia publicar**: o campo era
 * obrigatório, a cidade não estava na lista, e não havia como digitá-la. O
 * anunciante preenchia oito etapas para descobrir isso.
 *
 * O `<datalist>` resolve sem biblioteca: o campo continua um `<input>` de
 * texto — aceita qualquer valor, funciona com teclado, e o leitor de tela
 * anuncia como combobox —, e o navegador oferece as sugestões que vieram do
 * acervo. Quem anuncia em Itaúna escolhe "Itaúna" da lista e escreve igual a
 * todo mundo; quem anuncia em Formiga digita "Formiga", e a partir daí Formiga
 * passa a ser sugerida para o próximo.
 *
 * Estado NÃO usa isto, e a diferença é o ponto: são 27, a lista é completa, e
 * uma escolha fechada é a resposta certa quando a lista realmente acaba.
 */
function SuggestControl({ field, value, onChange, onBlur, error, disabled, options, inputId }: FieldComponentProps) {
  const listId = `${inputId}-sugestoes`;

  return (
    <>
      <Input
        id={inputId}
        list={listId}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={field.presentation.placeholder ?? undefined}
        maxLength={field.validation.maxLength ?? 120}
        autoComplete="off"
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy(inputId, field.presentation.help, error)}
      />
      <datalist id={listId}>
        {options.map((option) => (
          // `value` é o rótulo porque o datalist mostra e submete a mesma
          // coisa. É o que faz o anúncio guardar "Itaúna" e não "itauna" — o
          // slug vazando para dentro do nome foi um defeito real, e deixou o
          // acervo com a mesma cidade escrita de duas formas.
          <option key={option.value} value={option.label} />
        ))}
      </datalist>
    </>
  );
}

function RadioControl({ field, value, onChange, error, disabled, options, inputId }: FieldComponentProps) {
  return (
    <div className="ds-choice-list" role="radiogroup" aria-labelledby={`${inputId}-label`} aria-invalid={Boolean(error)}>
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <label key={option.value} className={`ds-choice ${selected ? 'ds-choice--selected' : ''}`.trim()}>
            <input
              type="radio"
              name={field.key}
              value={option.value}
              checked={selected}
              onChange={() => onChange(option.value)}
              disabled={disabled}
              className="ds-visually-hidden"
            />
            {option.label}
          </label>
        );
      })}
    </div>
  );
}

function MultiChoiceControl({ field, value, onChange, error, disabled, options, inputId }: FieldComponentProps) {
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const limit = field.validation.max;

  const toggle = (optionValue: string) => {
    if (selected.includes(optionValue)) {
      onChange(selected.filter((item) => item !== optionValue));
      return;
    }
    // Respeita o teto configurado (ex.: "escolha ate 3 aptidoes").
    if (limit !== null && selected.length >= limit) return;
    onChange([...selected, optionValue]);
  };

  return (
    <div className="ds-choice-list" role="group" aria-describedby={describedBy(inputId, field.presentation.help, error)}>
      {options.map((option) => {
        const isSelected = selected.includes(option.value);
        const blocked = !isSelected && limit !== null && selected.length >= limit;
        return (
          <label
            key={option.value}
            className={`ds-choice ${isSelected ? 'ds-choice--selected' : ''}`.trim()}
            style={blocked ? { opacity: 0.45 } : undefined}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggle(option.value)}
              disabled={disabled || blocked}
              className="ds-visually-hidden"
            />
            {option.label}
          </label>
        );
      })}
    </div>
  );
}

function BooleanControl({ field, value, onChange, disabled, inputId }: FieldComponentProps) {
  return (
    <label className="ds-switch" htmlFor={inputId}>
      <input
        id={inputId}
        type="checkbox"
        checked={value === true}
        onChange={(event) => onChange(event.target.checked)}
        disabled={disabled}
      />
      <span>{field.presentation.placeholder ?? 'Sim'}</span>
    </label>
  );
}

function DateControl({ field, value, onChange, onBlur, error, disabled, inputId }: FieldComponentProps) {
  return (
    <Input
      id={inputId}
      type={field.type === 'datetime' ? 'datetime-local' : 'date'}
      value={typeof value === 'string' ? value.slice(0, field.type === 'datetime' ? 16 : 10) : ''}
      onChange={(event) => onChange(event.target.value || null)}
      onBlur={onBlur}
      disabled={disabled}
      aria-invalid={Boolean(error)}
      aria-describedby={describedBy(inputId, field.presentation.help, error)}
    />
  );
}

function GeoPointControl({ value, onChange, disabled, inputId }: FieldComponentProps) {
  const point = (value ?? {}) as { latitude?: number; longitude?: number };

  return (
    <div className="ds-row">
      <Input
        id={inputId}
        type="number"
        step="any"
        placeholder="Latitude"
        value={point.latitude ?? ''}
        onChange={(event) => onChange({ ...point, latitude: event.target.value === '' ? null : Number(event.target.value) })}
        disabled={disabled}
        style={{ maxWidth: 200 }}
      />
      <Input
        type="number"
        step="any"
        placeholder="Longitude"
        value={point.longitude ?? ''}
        onChange={(event) => onChange({ ...point, longitude: event.target.value === '' ? null : Number(event.target.value) })}
        disabled={disabled}
        style={{ maxWidth: 200 }}
      />
    </div>
  );
}


/**
 * Upload real usa URL pre-assinada: o arquivo vai direto do navegador para o
 * object storage e a API so recebe a chave. O limite de fotos vem do plano
 * (`planLimits.maxPhotosPerListing`), injetado pelo SchemaForm em
 * `validation.max` — nao ha numero fixo aqui.
 */
function ComputedControl({ field, formValues, inputId }: FieldComponentProps) {
  const result = useMemo(() => {
    if (!field.computed) return null;
    return evaluateFormula(field.computed.formula, formValues);
  }, [field.computed, formValues]);

  if (result === null) {
    return (
      <output id={inputId} className="ds-field__help">
        Preencha os campos relacionados para calcular.
      </output>
    );
  }

  const isMoney = field.unit === 'BRL' || field.type === 'currency';
  return (
    <output id={inputId}>
      <Badge tone="primary">
        {isMoney ? formatCents(Math.round(result)) : formatNumber(result, field.computed?.precision ?? 2)}
        {field.unit && !isMoney ? ` ${field.unit}` : ''}
      </Badge>
    </output>
  );
}

let registered = false;

/** Chamado uma vez no bootstrap do app. */
export function registerBuiltinFields(): void {
  if (registered) return;
  registered = true;

  registerFieldType(['text', 'textarea', 'richtext', 'email', 'url', 'creci'], TextControl);
  registerFieldType(['cpf', 'cnpj', 'cep', 'phone', 'whatsapp'], MaskedControl);
  registerFieldType('password', PasswordControl);
  registerFieldType(['number', 'area', 'measure', 'percent'], NumericControl);
  registerFieldType('currency', CurrencyControl);
  registerFieldType(['select', 'state'], SelectControl);

  // Cidade e bairro aceitam valor novo. Ver `SuggestControl` — com a escolha
  // fechada, o primeiro anunciante de uma cidade nao conseguia publicar.
  registerFieldType(['city', 'neighborhood'], SuggestControl);
  registerFieldType('radio', RadioControl);
  registerFieldType(['multiselect', 'tags', 'checkbox'], MultiChoiceControl);
  registerFieldType('boolean', BooleanControl);
  registerFieldType(['date', 'datetime'], DateControl);
  registerFieldType('geopoint', GeoPointControl);
  // O esboco anterior gerava `blob:` local e nunca enviava nada — a foto
  // existia so na aba de quem preencheu. Agora sao dois componentes de
  // verdade, e o video tem o seu porque a interface e outra: um arquivo,
  // com player.
  registerFieldType(['gallery', 'image', 'document'], GalleryControl);
  registerFieldType('video', VideoControl);
  registerFieldType('computed', ComputedControl);
}
